// ─── Google Analytics 4 — centralised event helpers ──────────────────────────
//
// All gtag calls go through here so the rest of the app stays clean.
// Every function is a no-op when gtag isn't loaded (blocked / not yet init).

const GA_ID = "G-815TYT9S84";

const gtag = (...args) => {
  if (typeof window.gtag === "function") window.gtag(...args);
};

// ─── User identity ────────────────────────────────────────────────────────────
// Only send the internal numeric userId — never PII (mobile, name, email).
// GA4 Terms of Service prohibit sending personally identifiable information.
// To look up who a userId is, cross-reference with your own database.
export const gaIdentifyUser = ({ userId }) => {
  gtag("config", GA_ID, { user_id: String(userId) });
};

// Call on logout / session clear to reset identity.
export const gaResetUser = () => {
  gtag("config", GA_ID, { user_id: undefined });
};

// ─── Auth events ──────────────────────────────────────────────────────────────
export const gaLogin = ({ userId }) => {
  gtag("event", "login", {
    method:  "OTP",
    user_id: String(userId),
  });
};

// ─── Product events ───────────────────────────────────────────────────────────
export const gaViewProduct = ({ productId, name, price, unit, unitQty }) => {
  gtag("event", "view_item", {
    currency: "INR",
    value:    Number(price) || 0,
    items: [{
      item_id:   String(productId),
      item_name: name,
      price:     Number(price) || 0,
      item_variant: `${unitQty} ${unit}`,
    }],
  });
};

// ─── Cart events ──────────────────────────────────────────────────────────────
export const gaCartOpen = () => {
  const mobile = localStorage.getItem("userMobile");
  const name   = localStorage.getItem("userName");
  gtag("event", "cart_open", {
    user_type: mobile ? "logged_in" : "guest",
    mobile:    mobile || "guest",
    user_name: name   || "guest",
  });
};

export const gaAddToCart = ({ productId, name, price, qty, unit, unitQty, discount }) => {
  const finalPrice = discount > 0
    ? price - (price * discount / 100)
    : price;
  gtag("event", "add_to_cart", {
    currency: "INR",
    value:    Number((finalPrice * qty).toFixed(2)),
    items: [{
      item_id:      String(productId),
      item_name:    name,
      price:        Number(finalPrice.toFixed(2)),
      quantity:     qty,
      item_variant: `${unitQty} ${unit}`,
      discount:     Number(discount) || 0,
    }],
  });
};

export const gaRemoveFromCart = ({ productId, name, price, qty, unit, unitQty }) => {
  gtag("event", "remove_from_cart", {
    currency: "INR",
    value:    Number((price * qty).toFixed(2)),
    items: [{
      item_id:      String(productId),
      item_name:    name,
      price:        Number(price),
      quantity:     qty,
      item_variant: `${unitQty} ${unit}`,
    }],
  });
};

// ─── Checkout events ──────────────────────────────────────────────────────────
export const gaBeginCheckout = ({ cartItems, total, shippingCharge }) => {
  gtag("event", "begin_checkout", {
    currency: "INR",
    value:    Number(total.toFixed(2)),
    shipping: Number(shippingCharge.toFixed(2)),
    items: cartItems.map((item) => ({
      item_id:      String(item.productId),
      item_name:    item.name,
      price:        Number(item.price),
      quantity:     item.qty || 1,
      item_variant: `${item.unitQty} ${item.unit}`,
      discount:     Number(item.discount) || 0,
    })),
  });
};

export const gaPurchase = ({ orderId, cartItems, total, shippingCharge, shippingProvider }) => {
  gtag("event", "purchase", {
    transaction_id:    String(orderId),
    currency:          "INR",
    value:             Number(total.toFixed(2)),
    shipping:          Number((shippingCharge || 0).toFixed(2)),
    shipping_provider: shippingProvider || "unknown",
    items: cartItems.map((item) => ({
      item_id:      String(item.productId),
      item_name:    item.name,
      price:        Number(item.price),
      quantity:     item.qty || 1,
      item_variant: `${item.unitQty} ${item.unit}`,
      discount:     Number(item.discount) || 0,
    })),
  });
};
